import { useState, useEffect, useMemo, useCallback } from 'react';

import { UseQueryResult } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory, useLocation } from 'react-router-dom';

import { ContentType, LandingPage, Template } from 'domains/content/types';
import FolderNamingDialog from 'domains/core/components/folders/FolderNamingDialog';
import useCreateContentFolder from 'hooks/mutations/useCreateContentFolder';
import useArchivedContentList from 'hooks/queries/useArchivedContentList';
import useContentWithFolders from 'hooks/queries/useContentWithFolders';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import useGetFolderLineage from 'hooks/queries/useGetFolderLineage';
import usePaginatedArchivedContent from 'hooks/queries/usePaginatedArchivedContent';
import usePaginatedContent from 'hooks/queries/usePaginatedContent';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useContentQueryManager from 'hooks/useContentQueryParams';
import { SortDirection, URLPaths } from 'models/enums';
import { Folder, FolderLocation } from 'models/types';
import { ACCESS_CENTRE_LIST_PAGE_SIZE, ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS } from 'utils';
import { getFolderDepth, getIsFolderContentActive, isRootFolder, MAX_FOLDER_DEPTH } from 'utils/folders';

import ContentPageLayout from './ContentPageLayout';
import { contentTypeToLabel } from './ContentPageLayout/ContentPageLayout';
import useGetCreateContentDropdownOptions from './useGetCreateContentDropdownOptions';

const ContentPageWithFolders = () => {
    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings();
    const hasEmailContent = isFeatureSettingsSuccess && featureSettings?.email_enabled !== false;
    const hasSmsContent = isFeatureSettingsSuccess && featureSettings?.sms_enabled !== false;
    const hasDirectMailContent = isFeatureSettingsSuccess && featureSettings?.directmail_enabled;
    const hasVoiceContent = isFeatureSettingsSuccess && featureSettings.voice_enabled;
    const { whatsappContent: hasWhatsappContent, paginationFlag, contentSubFolder: hasContentSubFolder } = useFlags();
    const { canManageContent } = useAccessCentrePermissions();
    const useListPagination = Boolean(paginationFlag);

    const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] = useState(false);
    const [isArchivedView, setIsArchivedView] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(ACCESS_CENTRE_LIST_PAGE_SIZE);
    const [sortColumnState, setSortColumnState] = useState('last_modified_timestamp');
    const [sortDirectionState, setSortDirectionState] = useState(SortDirection.DESCENDING);

    const history = useHistory();
    const location = useLocation();
    const contentQueryManager = useContentQueryManager();
    const { changeViewParams, type, folderId, statuses } = contentQueryManager;

    const offset = (page - 1) * rowsPerPage;

    const legacyContentQuery = useContentWithFolders({
        folderId,
        type,
        searchTerm,
        statuses,
        reactQueryOptions: {
            enabled: !useListPagination && !isArchivedView,
        },
    });

    const paginatedContentQuery = usePaginatedContent({
        options: {
            folderId,
            type,
            searchTerm,
            statuses,
            limit: rowsPerPage,
            offset,
            sortColumn: sortColumnState || undefined,
            sortDirection: sortColumnState ? sortDirectionState : undefined,
        },
        reactQueryOptions: {
            enabled: useListPagination && !isArchivedView,
            keepPreviousData: true,
        },
    });

    const archivedContentQuery = useArchivedContentList({
        type,
        searchTerm,
        statuses,
        reactQueryOptions: { enabled: isArchivedView && !useListPagination },
    });

    const paginatedArchivedContentQuery = usePaginatedArchivedContent({
        options: {
            type,
            searchTerm,
            statuses,
            limit: rowsPerPage,
            offset,
            sortColumn: sortColumnState || undefined,
            sortDirection: sortColumnState ? sortDirectionState : undefined,
        },
        reactQueryOptions: {
            enabled: useListPagination && isArchivedView,
            keepPreviousData: true,
        },
    });

    const adaptedPaginatedContentQuery = useMemo(
        () => ({
            ...paginatedContentQuery,
            data: paginatedContentQuery.data?.data ?? [],
        }),
        [paginatedContentQuery]
    );

    const adaptedPaginatedArchivedContentQuery = useMemo(
        () => ({
            ...paginatedArchivedContentQuery,
            data: paginatedArchivedContentQuery.data?.data ?? [],
        }),
        [paginatedArchivedContentQuery]
    );

    const contentTotalCount = useListPagination
        ? isArchivedView
            ? paginatedArchivedContentQuery.data?.total
            : paginatedContentQuery.data?.total
        : undefined;
    const totalPages = Math.max(1, Math.ceil((contentTotalCount ?? 0) / rowsPerPage));

    useEffect(() => {
        if (!useListPagination) return;
        setPage(1);
    }, [folderId, type, statuses, searchTerm, isArchivedView, useListPagination]);

    useEffect(() => {
        if (!useListPagination) return;
        setPage(1);
    }, [rowsPerPage, useListPagination]);

    useEffect(() => {
        if (!useListPagination) return;
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [useListPagination, page, totalPages]);

    const handleContentSort = useCallback((sortColumn: string, direction: SortDirection) => {
        if (!sortColumn) {
            setSortColumnState('last_modified_timestamp');
            setSortDirectionState(SortDirection.DESCENDING);
        } else {
            setSortColumnState(sortColumn);
            setSortDirectionState(direction);
        }
        setPage(1);
    }, []);

    const handlePreviousPage = useCallback(() => {
        setPage((p) => (p > 1 ? p - 1 : p));
    }, []);

    const handleNextPage = useCallback(() => {
        setPage((p) => (p < totalPages ? p + 1 : p));
    }, [totalPages]);

    const handleRowsPerPageChange = useCallback((next: number) => {
        setRowsPerPage(next);
        setPage(1);
    }, []);

    // Check if we're on the archive URL and set state accordingly
    useEffect(() => {
        const isArchiveUrl = location.pathname === URLPaths.CONTENT_ARCHIVED;
        setIsArchivedView(isArchiveUrl);
    }, [location.pathname]);

    const refetchContent = useCallback(() => {
        if (isArchivedView) {
            if (useListPagination) {
                return paginatedArchivedContentQuery.refetch();
            }
            return archivedContentQuery.refetch();
        }
        if (useListPagination) {
            return paginatedContentQuery.refetch();
        }
        return legacyContentQuery.refetch();
    }, [
        isArchivedView,
        useListPagination,
        archivedContentQuery,
        paginatedArchivedContentQuery,
        paginatedContentQuery,
        legacyContentQuery,
    ]);

    const isUserOnRootLevel = isRootFolder(folderId);

    const { data: folderBreadcrumbData } = useGetFolderLineage({
        folderId,
        folderLocation: FolderLocation.CONTENT_FOLDER,
        reactQueryOptions: {
            enabled: hasContentSubFolder && getIsFolderContentActive(folderId),
        },
    });

    const currentFolderDepth = getFolderDepth(folderBreadcrumbData);
    const isAtMaxFolderDepth = currentFolderDepth >= MAX_FOLDER_DEPTH;
    const canCreateNestedFolder = hasContentSubFolder && !isUserOnRootLevel;

    const createContentDropdownOptions = [
        ...useGetCreateContentDropdownOptions(),
        ...(hasContentSubFolder || isUserOnRootLevel
            ? [
                  {
                      label: 'folder',
                      onClick: () => setIsCreateFolderModalVisible(true),
                      disabled: hasContentSubFolder && isAtMaxFolderDepth,
                      tooltip:
                          hasContentSubFolder && isAtMaxFolderDepth
                              ? `Maximum folder depth (${MAX_FOLDER_DEPTH}) reached`
                              : undefined,
                  },
              ]
            : []),
    ];

    const createFolder = (folderName: string) => {
        const body = {
            name: folderName,
            ...(canCreateNestedFolder ? { parentFolderId: folderId } : {}),
        };
        saveFolder(body);
    };

    const onCreateFolderSuccess = (response: { id: number }) => {
        changeViewParams({ newFolderId: response.id });
        refetchContent();
        setIsCreateFolderModalVisible(false);
    };

    const { mutate: saveFolder, isLoading: isFolderSaving } = useCreateContentFolder({
        onSuccess: onCreateFolderSuccess,
    });

    const handleToggleArchivedView = () => {
        const newArchivedView = !isArchivedView;
        setIsArchivedView(newArchivedView);

        // Update URL based on archived view state, preserving status filter
        if (newArchivedView) {
            const params = new URLSearchParams();
            if (type && type !== ContentType.ALL) {
                params.set('type', type);
            }
            if (statuses.length > 0) {
                params.set('status', statuses.join(','));
            }
            const queryString = params.toString();
            history.push(`${URLPaths.CONTENT_ARCHIVED}${queryString ? `?${queryString}` : ''}`);
        } else {
            // Use changeViewParams to properly handle folder paths (preserves status)
            changeViewParams({ newFolderId: folderId, newType: type });
        }
    };

    const currentContentQuery = isArchivedView
        ? useListPagination
            ? adaptedPaginatedArchivedContentQuery
            : archivedContentQuery
        : useListPagination
        ? adaptedPaginatedContentQuery
        : legacyContentQuery;

    const contentPaginationProps = useListPagination
        ? {
              page,
              totalPages,
              onPreviousPage: handlePreviousPage,
              onNextPage: handleNextPage,
              rowsPerPage,
              rowsPerPageOptions: ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS,
              onRowsPerPageChange: handleRowsPerPageChange,
          }
        : undefined;

    const handleTypeChange = (newType: ContentType) => {
        if (isArchivedView) {
            // If we're in archived view, navigate to archived URL with type parameter
            // Preserve status filter if present
            const params = new URLSearchParams();
            if (newType !== ContentType.ALL) {
                params.set('type', newType);
            }
            if (statuses.length > 0) {
                params.set('status', statuses.join(','));
            }
            const queryString = params.toString();
            history.push(`${URLPaths.CONTENT_ARCHIVED}${queryString ? `?${queryString}` : ''}`);
        } else {
            // If we're in regular view, use the normal changeViewParams which preserves status
            changeViewParams({ newType });
        }
    };

    const headerOtherDropdownProps = {
        label: contentTypeToLabel(type),
        options: [
            {
                label: contentTypeToLabel(ContentType.ALL),
                onClick: () => handleTypeChange(ContentType.ALL),
                selected: type === ContentType.ALL || !type,
            },
            // Don't show Folder option in archived view
            ...(isArchivedView
                ? []
                : [
                      {
                          label: contentTypeToLabel(ContentType.FOLDER),
                          onClick: () => handleTypeChange(ContentType.FOLDER),
                          selected: type === ContentType.FOLDER,
                      },
                  ]),
            ...(hasEmailContent
                ? [
                      {
                          label: contentTypeToLabel(ContentType.EMAIL),
                          onClick: () => handleTypeChange(ContentType.EMAIL),
                          selected: type === ContentType.EMAIL,
                      },
                  ]
                : []),
            ...(hasSmsContent
                ? [
                      {
                          label: contentTypeToLabel(ContentType.SMS),
                          onClick: () => handleTypeChange(ContentType.SMS),
                          selected: type === ContentType.SMS,
                      },
                  ]
                : []),
            ...(hasDirectMailContent
                ? [
                      {
                          label: contentTypeToLabel(ContentType.DIRECT_MAIL),
                          onClick: () => handleTypeChange(ContentType.DIRECT_MAIL),
                          selected: type === ContentType.DIRECT_MAIL,
                      },
                  ]
                : []),
            {
                dataTestId: 'view-landing-pages',
                label: contentTypeToLabel(ContentType.PAGE),
                onClick: () => handleTypeChange(ContentType.PAGE),
                selected: type === ContentType.PAGE,
            },
            ...(hasVoiceContent
                ? [
                      {
                          label: contentTypeToLabel(ContentType.VOICE),
                          onClick: () => handleTypeChange(ContentType.VOICE),
                          selected: type === ContentType.VOICE,
                      },
                  ]
                : []),
            ...(hasWhatsappContent
                ? [
                      {
                          label: contentTypeToLabel(ContentType.WHATSAPP),
                          onClick: () => handleTypeChange(ContentType.WHATSAPP),
                          selected: type === ContentType.WHATSAPP,
                      },
                  ]
                : []),
        ],
    };

    return (
        <>
            <FolderNamingDialog
                data-testid="folder-name-input"
                disableActionButton={isFolderSaving}
                initialValue="Untitled Folder"
                isDialogVisible={isCreateFolderModalVisible}
                onClose={() => setIsCreateFolderModalVisible(false)}
                onSubmit={createFolder}
                title="create new folder"
            />
            <ContentPageLayout
                contentQuery={currentContentQuery as UseQueryResult<(Template | LandingPage | Folder)[], unknown>}
                contentTotalCount={contentTotalCount}
                createContentDropdownOptions={createContentDropdownOptions}
                headerOtherDropdownProps={headerOtherDropdownProps}
                isNestedContent={!isUserOnRootLevel}
                isArchivedView={isArchivedView}
                onToggleArchivedView={handleToggleArchivedView}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                selectedStatuses={statuses}
                onStatusFilterApply={changeViewParams}
                canManageContent={canManageContent}
                contentPagination={contentPaginationProps}
                onContentSort={useListPagination ? handleContentSort : undefined}
                manualContentSort={useListPagination}
            />
        </>
    );
};

export default ContentPageWithFolders;
